import { ReactNode } from 'react';
import { motion } from 'framer-motion';
import { cn } from '../../lib/cn';
import { useReducedMotion } from '../../hooks/useReducedMotion';

type SystemFrameProps = {
  id: string;
  children: ReactNode;
  className?: string;
};

const frameVariants = {
  hidden: {
    opacity: 0,
    y: 42,
    clipPath: 'inset(6% 0% 8% 0% round 24px)'
  },
  show: {
    opacity: 1,
    y: 0,
    clipPath: 'inset(0% 0% 0% 0% round 24px)',
    transition: {
      duration: 0.95,
      ease: [0.2, 1, 0.32, 1],
      staggerChildren: 0.07
    }
  }
} as const;

export const SystemFrame = ({ id, children, className }: SystemFrameProps) => {
  const reducedMotion = useReducedMotion();
  const mobileViewport = typeof window !== 'undefined' && window.matchMedia('(max-width: 960px)').matches;

  return (
    <motion.section
      id={id}
      className={cn('relative px-4 py-20 sm:px-6 lg:px-10', className)}
      variants={frameVariants}
      initial={mobileViewport ? false : 'hidden'}
      whileInView={mobileViewport ? undefined : 'show'}
      animate={mobileViewport ? 'show' : undefined}
      viewport={mobileViewport ? undefined : { once: true, amount: 0.3 }}
    >
      <div className="hud-shell relative z-20">{children}</div>

      <motion.div
        className="pointer-events-none absolute left-2 top-6 h-px w-28 bg-gradient-to-r from-brand-lime/70 to-transparent sm:left-6"
        animate={reducedMotion ? undefined : { opacity: [0.3, 1, 0.35], scaleX: [0.75, 1, 0.8] }}
        transition={{ duration: 2.8, repeat: Infinity }}
      />

      <motion.div
        className="pointer-events-none absolute bottom-6 right-2 h-px w-28 bg-gradient-to-l from-brand-lime/70 to-transparent sm:right-6"
        animate={reducedMotion ? undefined : { opacity: [1, 0.35, 1], scaleX: [1, 0.72, 1] }}
        transition={{ duration: 2.8, repeat: Infinity, delay: 0.6 }}
      />

      <motion.div
        className="pointer-events-none absolute inset-y-12 left-[-10%] w-[16%] bg-[linear-gradient(90deg,transparent,rgba(183,255,106,0.18),transparent)] blur-sm"
        animate={reducedMotion ? undefined : { x: ['0%', '650%'], opacity: [0, 0.7, 0] }}
        transition={{ duration: 3.6, repeat: Infinity, ease: 'linear', repeatDelay: 2.1 }}
      />
    </motion.section>
  );
};
